// Offline "QA mode" for the email verification flow + account-page backup
// email screens — lets a reviewer walk every screen (signin methods, which
// prompt is due, the reauth modal, error states) without hitting the real API.
//
// Always on — this is deployed to its own dedicated review URL, so there's
// no real-user traffic to gate it away from.
const STORAGE_KEY = 'plickers:emailVerificationQA'

// fixed address used whenever QA mode seeds/creates a backup email, so it's
// always recognizable as mock data
export const MOCK_BACKUP_EMAIL = 'backup-email@plickers.com'

const defaultState = {
  password: true,
  externalSignin: 'none', // 'none' | 'google' | 'apple' — an account can't have both
  primaryState: 'none', // 'none' | 'verifyDue' | 'affirmDue' — mutually exclusive
  hasBackupEmail: false,
  // meaning depends on hasBackupEmail: with no backup email 'due' means
  // addBackup is due; with one set, 'due' means affirmBackup is due
  backupState: 'none', // 'none' | 'due'
  requireReauth: false,
}

// Kept in memory rather than localStorage (unlike the rest of QA state) so it
// actually resets on a page reload, matching qaCreateEmailVerification's "once
// per QA session" comment — persisting it would leave a stale reauthed:true
// around from an earlier test, silently defeating the requireReauth toggle.
let reauthed = false

export function deriveSigninMethods(qa) {
  return {
    password: !!qa.password,
    google: qa.externalSignin === 'google',
    apple: qa.externalSignin === 'apple',
  }
}

export function derivePrompts(qa) {
  return {
    verifyPrimary: qa.primaryState === 'verifyDue',
    affirmPrimary: qa.primaryState === 'affirmDue',
    addBackup: !qa.hasBackupEmail && qa.backupState === 'due',
    affirmBackup: qa.hasBackupEmail && qa.backupState === 'due',
  }
}

function readState() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch (e) {
    return {}
  }
}

function writeState(partial) {
  const next = { ...defaultState, ...readState(), ...partial }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
  } catch (e) {
    // ignore (private browsing, storage disabled, ...) — QA mode just won't persist
  }
  return next
}

// Always true — kept as a function (rather than a plain export) so call
// sites read the same either way if this ever needs gating again.
export function isEmailVerificationQAEnabled() {
  return typeof window !== 'undefined'
}

export function getEmailVerificationQAState() {
  return { ...defaultState, ...readState(), reauthed }
}

export function setEmailVerificationQAState(partial) {
  const { reauthed: nextReauthed, ...persisted } = partial
  if ('reauthed' in partial) {
    reauthed = nextReauthed
  }
  const next = writeState(persisted)
  return { ...next, reauthed }
}

export function resetEmailVerificationQAState() {
  try {
    window.localStorage.removeItem(STORAGE_KEY)
  } catch (e) {
    // ignore
  }
  reauthed = false
  return { ...defaultState, reauthed }
}

// A fake createEmailVerification record — shaped like the real API response.
export function fakeVerificationRecord(purpose) {
  const now = Date.now()
  return {
    id: `qa-${purpose}-${now}`,
    purpose,
    expiresAt: new Date(now + 10 * 60 * 1000).toISOString(),
    resendAvailableAt: new Date(now + 5 * 1000).toISOString(),
  }
}

// createEmailVerification only reauths for updatePrimary/upsertBackup, mirroring
// the real endpoint (verifyPrimary never needs it) — see emailVerification.js's
// `create`. Returns a rejection shaped like the real 403 reauth_required so the
// existing container error-handling (checking err.response.status) just works.
export function reauthRequiredError() {
  const error = new Error('reauth_required')
  error.response = { status: 403 }
  return error
}

// Type one of these into the verify-code screen in QA mode to force a
// specific failure instead of succeeding — lets a reviewer reach every error
// state getErrorMessageForRequest knows about for 'verifyEmailVerificationCode'
// without a live backend. Any other 6 digits (including the real-looking ones
// QA mode doesn't actually check) succeed.
export const MOCK_VERIFICATION_CODES = {
  INCORRECT: '000000',
  EXPIRED: '111111',
  RATE_LIMITED: '222222',
  NO_LONGER_PENDING: '333333',
  SERVER_ERROR: '999999',
}

const MOCK_VERIFICATION_CODE_STATUSES = {
  [MOCK_VERIFICATION_CODES.INCORRECT]: 422, // wrong code, attempts remain
  [MOCK_VERIFICATION_CODES.EXPIRED]: 419, // past the record's expiresAt
  [MOCK_VERIFICATION_CODES.RATE_LIMITED]: 429, // maxAttempts/maxSends exhausted
  [MOCK_VERIFICATION_CODES.NO_LONGER_PENDING]: 412, // discarded by a newer request, or already used
  [MOCK_VERIFICATION_CODES.SERVER_ERROR]: 500, // exercises the generic fallback message
}

// Returns an error shaped like the real API's rejection for the given status,
// or null if `code` isn't one of the magic ones.
export function mockVerificationCodeError(code) {
  const status = MOCK_VERIFICATION_CODE_STATUSES[code]
  if (!status) return null
  const error = new Error(`qa mock: verify ${status}`)
  error.response = { status }
  return error
}
